import type * as React from 'react';
import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';

import { Box } from '../../components/ui/box';
import { Checkbox } from '../../components/ui/checkbox';
import { Text } from '../../components/ui/text';
import { Toolbar } from '../../components/ui/toolbar';
import { DND_DRAGGABLE_PROPS, sortableStyle, withTouchCapture } from '../../lib/dnd-attrs';
import { eventShiftKey } from '../../lib/event-modifiers.ts';

import {
  useKanbanColumnItems,
  useKanbanDrag,
  useKanbanSelection,
} from './kanban-context';

// The controls ride ONE floating pill over the title row — the card reserves
// no width for it, so a long title runs underneath. The whole pill fades in
// together: on pointer hover, on KEYBOARD focus in the card (focus-visible, not
// focus-within — a mouse click on the checkbox leaves DOM focus on it, and
// focus-within would then pin the pill open after the pointer leaves), while one
// of its popups is open, and on a selected card (so its checkbox always
// reads), and on an ARMED card — the card a finger's HELD press highlighted
// on a touchscreen laptop (`lib/touch-arm`); the click it produces is spent,
// and a tap opens the card. A touch device never mounts the pill: the board
// drops selection there, so a finger's hold is the drag alone. An in-place
// title edit (data-card-editing) clears the pill entirely.
const CLUSTER_PILL_REVEAL =
  'opacity-0 transition-opacity group-hover/card:opacity-100 group-data-armed/card:opacity-100 group-has-focus-visible/card:opacity-100 has-[[data-popup-open]]:opacity-100 group-data-[selected]/card:opacity-100 group-has-[[data-card-editing]]/card:hidden';
// The popover chrome — the solid backing, border and lift — paints ONLY when
// the actions are showing (hover / keyboard focus / an open popup), because
// that is when the pill overlaps the title and needs to cover it. A selected-but-unhovered card shows a BARE checkbox with no backing. The
// 1px border and padding stay reserved (transparent) in every state, so the
// paint appears without moving the checkbox a pixel.
const CLUSTER_PILL_CHROME =
  'border border-transparent bg-transparent shadow-none group-hover/card:border-popover-border group-hover/card:bg-popover group-hover/card:shadow-popover group-data-armed/card:border-popover-border group-data-armed/card:bg-popover group-data-armed/card:shadow-popover group-has-focus-visible/card:border-popover-border group-has-focus-visible/card:bg-popover group-has-focus-visible/card:shadow-popover has-[[data-popup-open]]:border-popover-border has-[[data-popup-open]]:bg-popover has-[[data-popup-open]]:shadow-popover';
// Inside the pill the checkbox always stands, but the action buttons collapse
// out (display, not opacity — so the pill hugs the checkbox horizontally) until
// the card is hovered, takes KEYBOARD focus, or one of their popups is open. On
// a selected card at rest that leaves a tidy checkbox-only pill; engaging it
// grows the pill left. The checkbox wrapper holds the action-row height (h-7) in
// every state, so the pill's height never changes and the checkbox never rides
// up.
const CLUSTER_ACTIONS_REVEAL =
  'hidden group-hover/card:flex group-data-armed/card:flex group-has-focus-visible/card:flex has-[[data-popup-open]]:flex';
import { pressArmed } from '../../lib/touch-arm';
import { cardDndId } from './kanban-ids';
import type { Id } from './types';
import type { Omittable } from "../../lib/props";

export type KanbanCardProps<TItem> = Omittable<{
  item: TItem;
  columnId: Id;
  /**
   * Overrides the sortable id (default `cardDndId(columnId, itemId)`). The
   * destination-column drag phantom sets this to the ACTIVE card's own id so
   * dnd-kit recognizes it as the active node — which both lets
   * `verticalListSortingStrategy` open the cross-column gap and keeps it out of
   * collision detection (a mismatched id makes it a rogue drop target and the
   * over-column flip-flops). `data.columnId` stays the rendered column.
   */
  dndId?: Id;
  isDragOverlay?: boolean;
  /** Item id of the card above this one (for midpoint calculation). */
  upperNeighborId?: Id;
  /** Sort value of the card above this one. */
  upperSortValue?: number;
  /** Item id of the card below this one. */
  lowerNeighborId?: Id;
  /** Sort value of the card below this one. */
  lowerSortValue?: number;
}>;

/**
 * Sortable wrapper ONLY — zero card-field logic. Owns the dnd-kit wiring
 * (sortable transforms, click-vs-drag disambiguation) and the selection
 * chrome (the title-row actions + group-drag visuals), and
 * delegates the card body to `renderCard` from context. Generic over `TItem`.
 */
export function KanbanCard<TItem, TColumn>({
  item,
  columnId,
  dndId,
  isDragOverlay,
  upperNeighborId,
  upperSortValue,
  lowerNeighborId,
  lowerSortValue,
}: KanbanCardProps<TItem>) {
  const {
    getItemId,
    getSortValue,
    renderCard,
    onCardClick,
    draggable,
    isItemDragDisabled,
    dragGroupIds,
    getItemLabel,
  } = useKanbanDrag<TItem, TColumn>();
  const selection = useKanbanSelection<TItem>();
  const columnItems = useKanbanColumnItems<TItem>();
  const dragDisabled = isItemDragDisabled?.(item) ?? false;
  const itemId = getItemId(item);
  const isSelected = selection?.selectedItemIds.has(itemId) ?? false;

  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({
    id: dndId ?? cardDndId(columnId, itemId),
    data: {
      kind: 'item',
      item,
      columnId,
      sortValue: getSortValue(item),
      upperNeighborId,
      upperSortValue,
      lowerNeighborId,
      lowerSortValue,
    },
    disabled: isDragOverlay || dragDisabled || !draggable,
  });

  // Group-drag renderings. The active card's slots (its source position AND
  // the destination phantom, which registers under the active dnd id) both
  // report `isDragging`, so they collapse to ONE compact placeholder — the
  // whole selection previews as a single "N items" card, not N gaps. The other
  // selected cards stay put and dim like the active card's single-drag slot.
  const isGroupDrag = dragGroupIds != null;
  const showGroupPlaceholder = isGroupDrag && isDragging && !isDragOverlay;
  const isCoDragged =
    isGroupDrag && !isDragging && !isDragOverlay && dragGroupIds.has(itemId);

  // Transition only while actively sorting so cards shift smoothly; on drop, no
  // transition prevents a "snap back" animation since the optimistic cache
  // update already places items in their new positions.
  const dndTransform = sortableStyle(transform).transform;
  const dndTransition = isSorting ? transition : undefined;
  const dndOpacity = showGroupPlaceholder
    ? undefined
    : isDragging || isCoDragged
      ? 0.2
      : undefined;

  const inert = !draggable;
  const pressable = onCardClick != null;

  // The resting cursor names the COMMON action, and on a card that is the
  // press: `pointer` wherever a click opens it. The hand never shows at rest —
  // it is reserved for the overlay copy, the one node actually being dragged.
  // A resting `grab` advertised the rarest gesture on the card while the only
  // zones that said nothing were the ones a reader came for: every editor
  // inside the card carries its own pointer and wash, so the whitespace and
  // title were left announcing "drag me" over a target that opens.
  const cursor = isDragOverlay
    ? 'grabbing'
    : pressable
      ? 'pointer'
      : undefined;

  // Dynamic dnd values (per-frame transforms, measured transitions) are the
  // legitimate inline-style case — never classes.
  const dndStyle: React.CSSProperties = {
    transform: dndTransform,
    transition: dndTransition,
    opacity: dndOpacity,
  };

  const selectable = selection != null && selection.isItemSelectable(item);
  // The ONE selection gesture the card body and its checkbox share, so the
  // two paths cannot drift: shift ranges from the pivot in this column,
  // REPLACING the pivot's previous shift range; anything else — or a shift
  // with no usable pivot — toggles this card alone and makes it the pivot.
  const toggleSelection = (shift: boolean) => {
    if (selection == null) return;
    if (
      shift &&
      columnItems != null &&
      selection.selectRangeTo(item, columnId, columnItems())
    ) {
      return;
    }
    selection.onItemSelectedChange(item, !isSelected);
    selection.setSelectionAnchor(columnId, itemId);
  };
  // A menu subtree per windowed card is expensive. Mount the consumer action
  // after the first hover or focus. Keep the checkbox mounted so CSS can reveal
  // it without a render delay.
  const [actionsArmed, setActionsArmed] = useState(false);
  const armActions = () => {
    if (!actionsArmed && selection != null && !isDragOverlay) {
      setActionsArmed(true);
    }
  };
  const actionsNode =
    actionsArmed && selection != null && !isDragOverlay
      ? selection.cardActions(item)
      : null;
  const showCluster =
    !isDragOverlay &&
    !showGroupPlaceholder &&
    (selectable || actionsNode != null);

  return (
    <Box
      ref={setNodeRef}
      fill="width"
      shrink
      position="relative"
      group="card"
      // With no pill there is nothing for a hold to reveal: the hold is the
      // drag, and a release short of it is a plain press that opens the card.
      armOnHold={showCluster}
      data-selected={isSelected ? "" : undefined}
      // The landing marker for the board's drop settle: the overlay clone
      // flies to the card that carries the dropped item's id (the sortable id
      // is column-scoped, so a cross-column move is a different node).
      data-kanban-item-id={isDragOverlay ? undefined : String(itemId)}
      // The card holds its own controls, so it is a named group, not a
      // button (dnd-kit's `role="button"` is dropped above): focus on it reads
      // "<title>, sortable", and Enter opens it (the key handler below).
      role="group"
      aria-label={getItemLabel(item)}
      // dnd-kit's sortable attributes make this node focusable (tabIndex 0),
      // so a dialog opened from the card restores focus HERE on close — and
      // the next keydown (holding Shift to range-select) upgrades that focus
      // to :focus-visible, painting the global outline on a node whose focus
      // is already shown by its hover cluster (group-has-focus-visible). Same
      // reset BOX_BUTTON_RESET applies.
      className="outline-none"
      cursor={cursor}
      // The sortable attributes make a draggable card focusable; an inert
      // card that still opens on press has to be reachable the same way.
      tabIndex={inert && pressable ? 0 : undefined}
      // The card IS the column's scrollable content, so it must keep panning
      // (the draggable marker's CSS would set `touch-action: none`); the
      // board's hold-activated touch sensor prevents the scroll once a hold
      // completes, so a finger can both swipe the column and drag a card.
      touchAction={inert ? undefined : 'manipulation'}
      style={dndStyle}
      onPointerEnter={armActions}
      onFocus={armActions}
      // A shift press extends the browser's text selection from the last
      // click, painting a highlight across every card between — the range
      // select reads as broken under it. Capture phase: dnd-kit's listeners
      // spread below own the bubbling `onMouseDown`.
      onMouseDownCapture={(event: React.MouseEvent<HTMLElement>) => {
        if (event.shiftKey && selectable) event.preventDefault();
      }}
      onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
        // Enter / Space on the card ITSELF is the press. A key that reached
        // here from a control inside the card is that control's own — the
        // target check keeps a token's editor from also opening the card.
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
          return;
        if (onCardClick == null || isDragging) return;
        // Space would scroll the column.
        event.preventDefault();
        onCardClick(item);
      }}
      onClick={(event: React.MouseEvent<HTMLElement>) => {
        // React portals bubble through the REACT tree, not the DOM tree: a
        // click inside an overlay a card field opened (a dialog, a select
        // popup) re-fires here even though the DOM node lives under
        // document.body. Only a click physically inside the card counts.
        if (!event.currentTarget.contains(event.target as Node)) return;
        if (isDragging) return;
        // The click a held press produced arming a card with a control
        // pill is spent on showing the pill; a tap opens.
        if (showCluster && pressArmed(event.currentTarget)) {
          return;
        }
        // Cmd/ctrl+click toggles this card alone and makes it the pivot the
        // next shift+click ranges from — the Finder model. The checkbox is
        // the discoverable affordance, the modifier clicks are the fast ones.
        // Shift+click anywhere on the card ranges from the pivot.
        if (
          (event.metaKey || event.ctrlKey || event.shiftKey) &&
          selection != null &&
          selectable
        ) {
          // A held cmd/ctrl keeps the plain toggle, as before.
          toggleSelection(event.shiftKey && !event.metaKey && !event.ctrlKey);
          return;
        }
        onCardClick?.(item);
      }}
      {...(inert ? {} : DND_DRAGGABLE_PROPS)}
      {...(inert ? {} : (attributes as Record<string, unknown>))}
      {...(inert ? {} : withTouchCapture(listeners as Record<string, unknown>))}
    >
      {showGroupPlaceholder ? (
        <Box
          surface="card"
          rounded="md"
          align="center"
          justify="center"
          height={56}
          className="border"
        >
          <Text size="sm" variant="muted">
            {selection?.dragGroupLabel(dragGroupIds.size) ??
              `${dragGroupIds.size.toLocaleString()} items`}
          </Text>
        </Box>
      ) : (
        renderCard(item, { isDragOverlay: !!isDragOverlay, isSelected })
      )}
      {showCluster && (
        <Toolbar
          // A floating control cluster seated over the card's title row. It
          // reserves no card width — the title runs under it. When the actions
          // show it wears popover chrome (CLUSTER_PILL_CHROME) so the solid
          // backing covers the title beneath; a selected-but-unhovered card
          // shows just the bare checkbox with no backing. It fades in as one
          // unit (CLUSTER_PILL_REVEAL) and clears for an in-place title edit
          // (data-card-editing).
          className={`absolute top-1 right-1 flex items-center gap-0.5 rounded-lg p-0.5 text-popover-foreground ${CLUSTER_PILL_CHROME} ${CLUSTER_PILL_REVEAL}`}
          // The cluster owns its press (the click below never reaches the
          // card): a consumer card's open cue keys off this to stand down
          // while the pointer is over the cluster.
          data-owns-press=""
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          // Keep the PointerSensor from arming a drag under the controls; the
          // click above stops `onCardClick`.
          onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
        >
          {actionsNode != null && (
            <Box
              direction="row"
              align="center"
              gap="2xs"
              className={CLUSTER_ACTIONS_REVEAL}
            >
              {actionsNode}
            </Box>
          )}
          {selectable && (
            // Hold the action-row height (28px) whether or not the actions
            // show, so the pill's height is constant and the checkbox stays
            // vertically centered — no rise when the actions collapse out.
            <Box
              align="center"
              justify="center"
              height={28}
              paddingRight="2xs"
            >
              <Checkbox
                aria-label="Select card"
                checked={isSelected}
                // The toolbar stops this click before the card sees it, so
                // the box reads shift itself — shift+checking a second box
                // ranges, as in the table.
                onCheckedChange={(_checked, eventDetails) => {
                  toggleSelection(eventShiftKey(eventDetails.event));
                }}
              />
            </Box>
          )}
        </Toolbar>
      )}
    </Box>
  );
}
